import React, { useState, useEffect, Fragment, useContext } from 'react';
import moment from 'moment';
import get from 'lodash/get';
import round from 'lodash/round';
import { CloseOutline } from 'antd-mobile-icons';
import {
	List,
	Popup,
	NavBar,
	Calendar,
	TextArea,
	Button,
	Toast,
	Input,
	PickerView,
	Form,
	DatePickerView,
	Stepper,
	Switch,
	CheckList,
	SearchBar
} from 'antd-mobile';
import styled from 'styled-components';
import { SQL_DATE_FORMAT } from '../utils/constants';
import UserSelfContext from '../utils/self-context';
import { getIsRequired } from '../utils/helpers';

const InputContent = styled.div`
	background: #fff;
	margin-top: 12px;
	padding: 12px;
`;

const Save = styled(Button)`
	font-size: 17px;
`;

const Required = styled.span`
	display: inline-block;
	margin-left: 2px;
	color: #f5222d;
	font-size: 14px;
	line-height: 1;
`;

const ChecklistWrapper = styled.div`
	overflow-y: auto;
	height: calc(100vh - 130px);
    overflow-y: auto;
    margin-top: 16px;
`;


const Nav = styled(NavBar)`
	background: #fff;
	display: flex;
	border-bottom: solid 1px var(--adm-color-border);
`;

const ToastContent = styled.div`
	word-break: normal;
	text-align: center;
	width: 100%;
`;

const WhiteSpaceBreak = styled.div`
	white-space: pre-wrap;
	word-break: break-word;
`;

const TimepickerInfo = styled.div`
	flex: 1;
	text-align: center;
	color: var(--adm-color-weak);
`;

const TimepickerInfoWrapper = styled.div`
	display: flex;
`;

function InputComponent(props) {
	const {
		title,
		dataKey,
		secondKey,
		initialValue,
		type,
		placeholder,
		allowClear,
		requiredRulesObjectType,
		getCustomDisplayValue,
		digits = 0,
		step = 1,
		onSave,
		min,
		max,
		options,
		disabled
	} = props;
	let { isRequired } = props;
	const [saving, setSaving] = useState(false);
	const [value, setValue] = useState(initialValue);
	const [visible, setVisible] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const { userSelf } = useContext(UserSelfContext);

	const [form] = Form.useForm();

	if (requiredRulesObjectType) {
		isRequired = getIsRequired({ userSelf, objectType: requiredRulesObjectType, fieldKey: dataKey });
	}

	function onClose() {
		setVisible(false);
	}

	useEffect(() => {
		form.resetFields();
		setValue(initialValue);
		setSearchValue('');
	}, [initialValue, visible]);

	async function onClearClick() {
		setSaving(true);
		try {
			const data = {
				[dataKey]: null
			};

			if (secondKey) {
				data[secondKey] = null;
			}

			await onSave(data).catch(() => Toast.show({
				icon: 'fail',
				content: <ToastContent>
					Something went wrong, cannot save.
				</ToastContent>
			}));

			setSaving(false);
			setVisible(false);
		} catch (e) {
			setSaving(false);
		}
	}

	function getInputContent() {
		switch (type) {
		case 'date':
			return <Calendar
				selectionMode='single'
				defaultValue={new Date()}
				value={value}
				onChange={val => setValue(val)}
			/>;
		case 'datetime':
			return <Form form={form} layout='horizontal'>
				<Form.Item
					extra="HHmm"
					label="Time"
					name="time"
					validateTrigger="onChange"
					initialValue={initialValue ? moment(initialValue).format('HHmm') : undefined}
					rules={[
						{
							required: true,
							message: 'Please enter time in HHmm format. e.g. 1230 for 12:30'
						},
						{
							validator: (_, val) => {
								if (!val) return Promise.resolve();
								// Regex for HHmm: 00-23 for HH, 00-59 for mm
								const isValid = /^([01]\d|2[0-3])[0-5]\d$/.test(val);
								return isValid
									? Promise.resolve()
									: Promise.reject(new Error('Invalid format'));
							}
						}
					]}
				>
					<Input
						clearable
					/>
				</Form.Item>
				<Form.Item
					label="Date"
				>
					<TimepickerInfoWrapper>
						<TimepickerInfo>
							Year
						</TimepickerInfo>
						<TimepickerInfo>
							Month
						</TimepickerInfo>
						<TimepickerInfo>
							Day
						</TimepickerInfo>
					</TimepickerInfoWrapper>
					<DatePickerView
						mode="date"
						value={value || new Date()}
						min={min}
						max={max}
						onChange={setValue}
					/>
				</Form.Item>
				{allowClear && <Form.Item>
					<Button loading={saving} block color='primary' fill='outline' onClick={onClearClick}>
						Clear
					</Button>
				</Form.Item>}
			</Form>;
		case 'select':
			return <PickerView
				columns={[options]}
				value={value}
				defaultValue={initialValue}
				onChange={setValue}
			/>;
		case 'multiple_select':
			return <Fragment>
				<SearchBar
					placeholder='Search'
					value={searchValue}
					onChange={v => setSearchValue(v)}
				/>
				<ChecklistWrapper>
					<CheckList
						multiple
						value={value.map(v => v.value)}
						onChange={(values) => {
							const selectedOptions = options.filter(o => values.includes(o.value));
							setValue(selectedOptions);
						}}
						defaultValue={initialValue ? initialValue.map(v => v.value) : []}
					>
						{options
							.filter(o => !searchValue || o.label.toLowerCase()
								.includes(searchValue.toLowerCase()))
							.map(o => <CheckList.Item key={o.value} value={o.value}>{o.label}</CheckList.Item>)}
					</CheckList>
				</ChecklistWrapper>
			</Fragment>;
		case 'switch':
			return <Switch
				checkedText="Yes"
				uncheckedText="No"
				checked={value}
				onChange={setValue}
			/>;
		case 'text':
			return <TextArea onChange={val => setValue(val)} value={value || ''} placeholder={placeholder} rows={5} />;
		case 'stepper':
			return <Stepper
				style={{ width: '100%' }}
				digits={digits}
				min={min}
				step={step}
				defaultValue={initialValue}
				value={value || 0}
				onChange={setValue}
			/>;
		default:
			return <Input
				type={type === 'number' ? 'number' : 'text'}
				placeholder={placeholder}
				value={value || ''}
				onChange={val => setValue(val)}
			/>;
		}
	}

	async function onSaveClick() {
		setSaving(true);
		let saveValue = value;
		let secondValue;

		try {
			switch (type) {
			case 'date':
				saveValue = value ? moment(value).format(SQL_DATE_FORMAT) : null;
				break;
			// eslint-disable-next-line no-case-declarations
			case 'datetime':
				const fields = await form.validateFields();
				const time = `${fields.time.slice(0, 2)}:${fields.time.slice(2, 4)}:00`;
				const date = moment(value || new Date()).format(SQL_DATE_FORMAT);

				if (secondKey) {
					saveValue = date;
					secondValue = time;
				} else {
					saveValue = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm:ss');
				}

				break;
			case 'select':
				[saveValue] = value;
				break;
			default:
				break;
			}

			const data = {
				[dataKey]: saveValue
			};

			if (secondKey) {
				data[secondKey] = secondValue;
			}

			await onSave(data).catch(() => Toast.show({
				icon: 'fail',
				content: <ToastContent>
					Something went wrong, cannot save.
				</ToastContent>
			}));

			setSaving(false);
			setVisible(false);
		} catch (e) {
			setSaving(false);
		}
	}

	function getInitialValue() {
		if (getCustomDisplayValue) {
			return getCustomDisplayValue(value);
		}
		switch (type) {
		case 'date':
			return initialValue ? moment(initialValue).format(get(userSelf.settings, 'date_format')) : '-';
		case 'datetime':
			return initialValue ? moment(initialValue).format(`${get(userSelf.settings, 'date_format')} HH:mm`) : '-';
		case 'select':
			return initialValue[0] || '-';
		case 'multiple_select':
			return (initialValue && initialValue.length) ? initialValue.map(v => v.label).join(', ') : '-';
		case 'stepper':
			return round(initialValue || 0, digits);
		case 'switch':
			return initialValue ? 'Yes' : 'No';
		case 'text':
			return <WhiteSpaceBreak>{initialValue || '-'}</WhiteSpaceBreak>;
		default:
			return initialValue || '-';
		}
	}

	return (
		<Fragment>
			<List header={<Fragment>
				{title}
				{isRequired ? <Required>*</Required> : null}
			</Fragment>}>
				<List.Item
					onClick={() => (disabled ? null : setVisible(true))}
					clickable={!disabled}
				>
					{getInitialValue()}
				</List.Item>
			</List>
			<Popup
				destroyOnClose
				position='right'
				visible={visible}
				bodyStyle={{ width: '100%', background: '#f0f2f5' }}
				onClose={onClose}
			>
				<Nav
					backArrow={<CloseOutline />}
					onBack={onClose}
					back={''}
					right={<Save fill='none' loading={saving} onClick={onSaveClick}>Save</Save>}
				>
					{title}
				</Nav>
				<InputContent>
					{getInputContent()}
				</InputContent>
			</Popup>
		</Fragment>
	);
}

export default InputComponent;
